/**
 * kittenLure.ts — Ugly Kittens Monkey-Bomb-style enemy lure
 *
 * BETA 16.69 — Per Dylan: "we need to upgrade the kittens that enemies
 * are drawn do it like monkeys in call of duty zombies. there should
 * be a max of how many zombies one kitten placement wil attract. and
 * then upgrades for the kittens will be a larger ammount and suck. and
 * larger attaack radious."
 *
 * Each frame, before enemy AI ticks, this module scans every alive
 * kitten projectile (weaponId = 'wpn_ugly_kittens', speed === 0) and
 * builds an assignment map:
 *   enemyId -> { x, y } — the kitten the enemy should steer toward.
 *
 * For each kitten:
 *   1. Find all alive enemies within lureRadius.
 *   2. Sort by distance.
 *   3. Take the top maxLured (closest first).
 *   4. Each becomes "attached" to that kitten THIS frame.
 *
 * Enemies already assigned to a closer kitten are skipped — multiple
 * kittens compete by closeness (no double-tugging).
 *
 * The enemy AI hook (steerEnemyAI in enemySystem.ts) consults
 * getKittenLureTarget() and overrides the steer target when there's a
 * match. Lure stats scale with weapon level via baseLureRadius +
 * lureRadiusPerLevel + baseMaxLured + maxLuredPerLevel on the weapon
 * def.
 */

import type { WeaponSystemState } from './weaponSystem';

/** Minimal duck-type for what tickKittenLure reads off each enemy.
 *  Both EnemyInstance (enemySystem) and Enemy (types.ts) satisfy this,
 *  so callers can pass either without a cast. */
interface LureCandidateEnemy {
  id: number;
  x: number;
  y: number;
  alive: boolean;
}

// Singleton frame-scoped assignment. Built fresh by tickKittenLure() each
// frame. Read by enemySystem during AI tick.
const _assignment = new Map<number, { x: number; y: number; kittenId: number }>();

/**
 * Optional scoped getters so this module doesn't have to know about the
 * weapon def lookup. Engine sets these once at boot.
 */
let _getWeaponDef: ((id: string) => unknown | undefined) | null = null;
let _getWeaponLevel: ((id: string) => number) | null = null;
export function registerKittenLureLookups(
  getWeaponDef: (id: string) => unknown | undefined,
  getWeaponLevel: (id: string) => number,
): void {
  _getWeaponDef = getWeaponDef;
  _getWeaponLevel = getWeaponLevel;
}

interface LureStats { lureRadius: number; maxLured: number; }

function readLureStats(): LureStats | null {
  if (!_getWeaponDef || !_getWeaponLevel) return null;
  const def = _getWeaponDef('wpn_ugly_kittens') as
    | { baseLureRadius?: number; lureRadiusPerLevel?: number; baseMaxLured?: number; maxLuredPerLevel?: number }
    | undefined;
  if (!def) return null;
  const level = Math.max(1, _getWeaponLevel('wpn_ugly_kittens') ?? 1);
  const lureRadius = (def.baseLureRadius ?? 0) + (def.lureRadiusPerLevel ?? 0) * (level - 1);
  const maxLured = Math.max(0, Math.floor((def.baseMaxLured ?? 0) + (def.maxLuredPerLevel ?? 0) * (level - 1)));
  if (lureRadius <= 0 || maxLured <= 0) return null;
  return { lureRadius, maxLured };
}

/**
 * Per-frame entry point. Called from updateGame BEFORE enemy AI ticks.
 * Walks every alive kitten projectile, picks lure targets, builds the
 * frame-scoped assignment map.
 */
export function tickKittenLure(weaponState: WeaponSystemState | null, enemies: readonly LureCandidateEnemy[]): void {
  _assignment.clear();
  if (!weaponState) return;
  const stats = readLureStats();
  if (!stats) return;

  // Find all alive kitten traps (speed=0 projectiles with the kitten weaponId).
  const kittens: { x: number; y: number; id: number }[] = [];
  for (const proj of weaponState.projectiles) {
    if (!proj.alive) continue;
    if (proj.weaponId !== 'wpn_ugly_kittens') continue;
    if (proj.speed !== 0) continue;
    kittens.push({ x: proj.x, y: proj.y, id: proj.id });
  }
  if (kittens.length === 0) return;

  const lureRadiusSq = stats.lureRadius * stats.lureRadius;
  // Track per-enemy best (closest) kitten distance² so multiple kittens don't
  // tug the same enemy in different directions — closest wins.
  const bestDist = new Map<number, number>();

  for (const kit of kittens) {
    // Collect candidates within range.
    const candidates: { id: number; d2: number }[] = [];
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.x - kit.x;
      const dy = e.y - kit.y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= lureRadiusSq) continue;
      candidates.push({ id: e.id, d2 });
    }
    if (candidates.length === 0) continue;
    candidates.sort((a, b) => a.d2 - b.d2);

    const limit = Math.min(stats.maxLured, candidates.length);
    for (let i = 0; i < limit; i++) {
      const c = candidates[i];
      const prev = bestDist.get(c.id);
      if (prev === undefined || c.d2 < prev) {
        bestDist.set(c.id, c.d2);
        _assignment.set(c.id, { x: kit.x, y: kit.y, kittenId: kit.id });
      }
    }
  }
}

/** Query: does this enemy currently have a kitten lure target? */
export function getKittenLureTarget(enemyId: number): { x: number; y: number } | null {
  const a = _assignment.get(enemyId);
  if (!a) return null;
  return { x: a.x, y: a.y };
}

/**
 * Total count of currently-lured enemies — used by render code to
 * potentially show a "lured: 3 / 8" badge above each kitten in the
 * future. Cheap O(1) — Map.size.
 */
export function getActiveLuredCount(): number {
  return _assignment.size;
}

/**
 * Helper: returns the live lure radius for the current weapon level.
 * Used by render code to draw the suck radius. Returns 0 if Ugly
 * Kittens is not equipped or has no lure config.
 */
export function getCurrentLureRadius(): number {
  const stats = readLureStats();
  return stats?.lureRadius ?? 0;
}
